import React, { useContext, useEffect, useState } from 'react'
import { AuthContext } from '../../context/auth/auth-context'
import { useSearchParams } from 'react-router-dom'
import { usePortfolios } from '../../hooks/data/use-portfolios'
import { useHeartbeatsImmutable } from '../../hooks/data/use-heartbeats'
import { isEmptyString } from '../../utils/string-utils'
import StrategyTradeBookComposite, { DISPLAY_TYPE } from './strategy-trade-book-composite'
import { FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material'
import EnsembleModalTrigger from '../strategy/modal/ensemble/ensemble-modal-trigger'
import { useEnsembles } from '../../hooks/data/use-ensembles'

const SelectableStrategyComposite: React.FC = () => {
  const { isAuthorized, accessToken } = useContext(AuthContext)
  const portfolios = usePortfolios(isAuthorized, accessToken)
  const heartbeats = useHeartbeatsImmutable(isAuthorized, accessToken)
  const [selectedPortfolio, setSelectedPortfolio] = useState<string>('')
  const [selectedCoin, setSelectedCoin] = useState<string>('')
  const [selectedStrategyId, setSelectedStrategyId] = useState<string>('')
  const [selectedStrategyName, setSelectedStrategyName] = useState<string>('')
  const [selectedDisplayType, setSelectedDisplayType] = useState<DISPLAY_TYPE>(DISPLAY_TYPE.DETAILED)
  const ensembles = useEnsembles(isAuthorized, accessToken)

  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    if (searchParams.has('portfolio')) {
      setSelectedPortfolio(searchParams.get('portfolio') as string)
    }
    if (searchParams.has('coin')) {
      setSelectedCoin(searchParams.get('coin') as string)
    }
    if (searchParams.has('strategy')) {
      setSelectedStrategyId(searchParams.get('strategy') as string)
    }
  }, [portfolios])

  useEffect(() => {
    if (isEmptyString(selectedStrategyId)) {
      setSearchParams({})
    } else {
      setSearchParams({
        portfolio: selectedPortfolio,
        coin: selectedCoin,
        strategy: selectedStrategyId,
      })
    }
  }, [selectedPortfolio, selectedCoin, selectedStrategyId])

  useEffect(() => {
    const matchingHeartbeat = heartbeats.find(item => item.strategyId === selectedStrategyId)
    let displayName = selectedStrategyId
    if (matchingHeartbeat != null) {
      displayName = isEmptyString(matchingHeartbeat.strategyName) ? selectedStrategyId : `${matchingHeartbeat.strategyName} (${selectedStrategyId})`
    }
    setSelectedStrategyName(displayName)
  }, [heartbeats, selectedStrategyId])

  const portfolioSelect = React.useMemo(() => {
    return (
      <Grid item xs={12} md={3} lg={2} xl={1}>
        <FormControl variant='standard' fullWidth>
          <InputLabel id='portfolio-select-label'>Portfolio</InputLabel>
          <Select
            labelId='portfolio-select-label'
            id='portfolio-select'
            label='Portfolio'
            value={selectedPortfolio}
            onChange={event => {
              setSelectedStrategyId('')
              setSelectedCoin('')
              setSelectedPortfolio(event.target.value)
            }}
          >
            {
              Object.keys(portfolios)
                .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                .map(key => {
                  return <MenuItem key={key} value={key}>{key}</MenuItem>
                })
            }
            {/* Suppress the out-of-range value warning */}
            {selectedPortfolio !== '' && (
              <MenuItem key={selectedPortfolio} value={selectedPortfolio} sx={{ display: 'none' }}>
                {selectedPortfolio}
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Grid>
    )
  }, [portfolios, selectedPortfolio])

  const coinSelect = React.useMemo(() => {
    const isDisabled = isEmptyString(selectedPortfolio)
    const coinToStrategies: Record<string, string[]> = portfolios[selectedPortfolio] ?? {}

    return (
      <Grid item xs={12} md={3} lg={2} xl={1}>
        <FormControl variant='standard' fullWidth disabled={isDisabled}>
          <InputLabel id='coin-select-label'>Coin</InputLabel>
          <Select
            labelId='coin-select-label'
            id='coin-select'
            label='Coin'
            value={selectedCoin}
            onChange={event => {
              setSelectedCoin(event.target.value)
              if (portfolios[selectedPortfolio]?.[event.target.value]?.length === 1) {
                setSelectedStrategyId(portfolios[selectedPortfolio]?.[event.target.value][0])
              } else {
                setSelectedStrategyId('')
              }
            }}
          >
            {
              Object.keys(coinToStrategies)
                .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                .map(key => {
                  return <MenuItem key={key} value={key}>{key}</MenuItem>
                })
            }
            {/* Suppress the out-of-range value warning */}
            {selectedCoin !== '' && (
              <MenuItem key={selectedCoin} value={selectedCoin} sx={{ display: 'none' }}>
                {selectedCoin}
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Grid>
    )
  }, [portfolios, selectedPortfolio, selectedCoin])

  const strategySelect = React.useMemo(() => {
    const isDisabled = isEmptyString(selectedCoin)
    const strategies: string[] = portfolios[selectedPortfolio]?.[selectedCoin] ?? []

    return (
      <Grid item xs={12} md={3} lg={2} xl={1}>
        <FormControl variant='standard' fullWidth disabled={isDisabled}>
          <InputLabel id='strategy-select-label'>Strategy</InputLabel>
          <Select
            labelId='strategy-select-label'
            id='strategy-select'
            label='Strategy'
            value={selectedStrategyId}
            onChange={event => {
              setSelectedStrategyId(event.target.value)
            }}
          >
            {
              strategies
                .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                .map(key => {
                  const matchingHeartbeat = heartbeats.find(item => item.strategyId === key)
                  let displayName = key
                  if (matchingHeartbeat != null) {
                    displayName = isEmptyString(matchingHeartbeat.strategyName) ? key : `${matchingHeartbeat.strategyName} (${key})`
                  }
                  return <MenuItem key={key} value={key}>{displayName}</MenuItem>
                })
            }
            {/* Suppress the out-of-range value warning */}
            {selectedStrategyId !== '' && (
              <MenuItem key={selectedStrategyId} value={selectedStrategyId} sx={{ display: 'none' }}>
                {selectedStrategyName}
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Grid>
    )
  }, [portfolios, heartbeats, selectedPortfolio, selectedCoin, selectedStrategyId, selectedStrategyName])

  const displayTypeSelect = React.useMemo(() => {
    return (
      <Grid item xs={12} md={3} lg={2} xl={1}>
        <FormControl variant='standard' fullWidth>
          <InputLabel id='display-type-select-label'>Display Type</InputLabel>
          <Select
            labelId='display-type-select-label'
            id='display-type-select'
            label='Display Type'
            value={selectedDisplayType}
            onChange={event => {
              setSelectedDisplayType(event.target.value as DISPLAY_TYPE)
            }}
          >
            {
              Object.entries(DISPLAY_TYPE).map(entry => {
                return (
                  <MenuItem key={entry[0]} value={entry[1]}>{entry[1]}</MenuItem>
                )
              })
            }
          </Select>
        </FormControl>
      </Grid>
    )
  }, [selectedDisplayType])

  return (
    <Grid
      container
      justifyContent='center'
    >
      <Grid
        item xs={12}
        container padding={2} columnSpacing={2} justifyContent='center'
      >
        { portfolioSelect }
        { coinSelect }
        { strategySelect }
        { displayTypeSelect }

        <Grid item>
          <EnsembleModalTrigger
            portfolioId={selectedPortfolio}
            strategyCoin={selectedCoin}
            strategyId={selectedStrategyId}
            ensembles={ensembles}
          />
        </Grid>
      </Grid>

      <Grid item xs>
        {
          isEmptyString(selectedStrategyId)
            ? null
            : <StrategyTradeBookComposite
              strategyId={selectedStrategyId}
              strategyName={selectedStrategyName}
              displayType={selectedDisplayType}
            />
        }
      </Grid>
    </Grid>
  )
}

export default SelectableStrategyComposite
